/**
 * Configuration Services Module
 * Centralized configuration management for the application
 */

class ConfigServices {
  constructor() {
    // Initialize configuration
    this.config = {
      app: {
        name: 'AWS Serverless Dashboard',
        version: '1.0.0',
        environment: this.getEnvironment(),
        debug: this.isDebugMode(),
      },
      api: {
        baseURL: this.getApiBaseURL(),
        timeout: 30000,
        retryAttempts: 3,
        retryDelay: 1000,
      },
      map: {
        defaultCenter: [34.0195, -118.4912], // Santa Monica
        defaultZoom: 12,
        maxZoom: 18,
        minZoom: 3,
        tileLayer: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
        attribution: '© OpenStreetMap contributors',
        clusterOptions: {
          showCoverageOnHover: false,
          zoomToBoundsOnClick: true,
          spiderfyOnMaxZoom: true,
          removeOutsideVisibleBounds: true,
          maxClusterRadius: 80,
        },
      },
      charts: {
        defaultColors: [
          '#2196f3', // Blue
          '#4caf50', // Green
          '#ff9800', // Orange
          '#f44336', // Red
          '#9c27b0', // Purple
          '#00bcd4', // Cyan
          '#ffeb3b', // Yellow
          '#795548', // Brown
        ],
        animationDuration: 750,
        responsive: true,
        maintainAspectRatio: false,
      },
      table: {
        pageSize: 10,
        pageSizeOptions: [5, 10, 25, 50, 100],
        density: 'standard', // 'compact' | 'standard' | 'comfortable'
        showGridLines: true,
        enableSorting: true,
        enableFiltering: true,
        enableColumnResize: true,
      },
      features: {
        enableAnalytics: true,
        enableNotifications: true,
        enableAutoSave: false,
        enableOfflineMode: false,
        enableDataExport: true,
        enableDataImport: false,
        enableRealTimeUpdates: false,
      },
      ui: {
        theme: 'light', // 'light' | 'dark' | 'auto'
        primaryColor: '#1976d2',
        secondaryColor: '#dc004e',
        fontFamily: 'Roboto, Arial, sans-serif',
        borderRadius: 4,
        elevation: {
          low: 2,
          medium: 4,
          high: 8,
        },
      },
      validation: {
        maxFileSize: 10 * 1024 * 1024, // 10MB
        allowedFileTypes: ['csv', 'xlsx', 'json'],
        maxBatchSize: 1000,
        requiredFields: ['stationid', 'latitude', 'longitude'],
      },
      cache: {
        enabled: true,
        ttl: 5 * 60 * 1000, // 5 minutes
        maxSize: 50, // Maximum number of cached items
        storageKey: 'aws-serverless-cache',
      },
      monitoring: {
        logLevel: this.getLogLevel(),
        enablePerformanceTracking: true,
        enableErrorTracking: true,
        enableUserTracking: false,
      },
    };

    // Load user preferences if available
    this.loadUserPreferences();
  }

  /**
   * Get current environment based on window location
   * @returns {string} Environment name
   */
  getEnvironment() {
    // Determine environment based on hostname
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return 'development';
      } else if (hostname.includes('staging') || hostname.includes('test')) {
        return 'staging';
      } else {
        return 'production';
      }
    }
    
    return 'development';
  }

  /**
   * Check if debug mode is enabled
   * @returns {boolean} Debug mode status
   */
  isDebugMode() {
    // Check localStorage for debug flag or if in development
    const debugFlag = localStorage.getItem('debug') === 'true';
    return this.getEnvironment() === 'development' || debugFlag;
  }

  /**
   * Get API base URL based on environment
   * @returns {string} API base URL
   */
  getApiBaseURL() {
    // Check localStorage for custom API URL
    const customApiUrl = localStorage.getItem('apiUrl');
    if (customApiUrl) {
      return customApiUrl;
    }

    const env = this.getEnvironment();
    
    switch (env) {
      case 'production':
        return 'https://s3fuxkjf49.execute-api.us-west-2.amazonaws.com/Prod';
      case 'staging':
        return 'https://staging-api.yourdomain.com';
      case 'development':
      default:
        return '/api';
    }
  }

  /**
   * Get log level based on environment
   * @returns {string} Log level
   */
  getLogLevel() {
    const env = this.getEnvironment();
    
    switch (env) {
      case 'production':
        return 'error';
      case 'staging':
        return 'warning';
      case 'development':
      default:
        return 'debug';
    }
  }

  /**
   * Load user preferences from localStorage
   */
  loadUserPreferences() {
    try {
      const stored = localStorage.getItem('user-preferences');
      if (stored) {
        const preferences = JSON.parse(stored);
        this.mergeConfig(preferences);
      }
    } catch (error) {
      console.error('Failed to load user preferences:', error);
    }
  }

  /**
   * Save user preferences to localStorage
   * @param {Object} preferences - User preferences to save
   */
  saveUserPreferences(preferences) {
    try {
      localStorage.setItem('user-preferences', JSON.stringify(preferences));
      this.mergeConfig(preferences);
    } catch (error) {
      console.error('Failed to save user preferences:', error);
    }
  }

  /**
   * Merge configuration updates
   * @param {Object} updates - Configuration updates
   */
  mergeConfig(updates) {
    this.config = this.deepMerge(this.config, updates);
  }

  /**
   * Deep merge two objects
   * @param {Object} target - Target object
   * @param {Object} source - Source object
   * @returns {Object} Merged object
   */
  deepMerge(target, source) {
    const output = { ...target };
    
    if (this.isObject(target) && this.isObject(source)) {
      Object.keys(source).forEach(key => {
        if (this.isObject(source[key])) {
          if (!(key in target)) {
            Object.assign(output, { [key]: source[key] });
          } else {
            output[key] = this.deepMerge(target[key], source[key]);
          }
        } else {
          Object.assign(output, { [key]: source[key] });
        }
      });
    }
    
    return output;
  }

  /**
   * Check if value is an object
   * @param {*} item - Item to check
   * @returns {boolean} True if object
   */
  isObject(item) {
    return item && typeof item === 'object' && !Array.isArray(item);
  }

  /**
   * Get full configuration
   * @returns {Object} Full configuration object
   */
  getConfig() {
    return this.config;
  }

  /**
   * Get specific configuration section
   * @param {string} section - Configuration section
   * @returns {Object} Configuration section
   */
  getConfigSection(section) {
    return this.config[section] || {};
  }

  /**
   * Get specific configuration value
   * @param {string} path - Configuration path (e.g., 'api.timeout')
   * @returns {*} Configuration value
   */
  get(path) {
    return path.split('.').reduce((obj, key) => obj?.[key], this.config);
  }

  /**
   * Set specific configuration value
   * @param {string} path - Configuration path
   * @param {*} value - Configuration value
   */
  set(path, value) {
    const keys = path.split('.');
    const lastKey = keys.pop();
    const target = keys.reduce((obj, key) => {
      if (!obj[key]) obj[key] = {};
      return obj[key];
    }, this.config);
    
    target[lastKey] = value;
  }

  /**
   * Get API configuration
   * @returns {Object} API configuration
   */
  getApiConfig() {
    return this.config.api;
  }

  /**
   * Get map configuration
   * @returns {Object} Map configuration
   */
  getMapConfig() {
    return this.config.map;
  }

  /**
   * Get chart configuration
   * @returns {Object} Chart configuration
   */
  getChartConfig() {
    return this.config.charts;
  }

  /**
   * Get table configuration
   * @returns {Object} Table configuration
   */
  getTableConfig() {
    return this.config.table;
  }

  /**
   * Get UI configuration
   * @returns {Object} UI configuration
   */
  getUIConfig() {
    return this.config.ui;
  }

  /**
   * Get feature flags
   * @returns {Object} Feature flags
   */
  getFeatureFlags() {
    return this.config.features;
  }

  /**
   * Check if feature is enabled
   * @param {string} feature - Feature name
   * @returns {boolean} Feature status
   */
  isFeatureEnabled(feature) {
    return this.config.features[feature] === true;
  }

  /**
   * Get validation rules
   * @returns {Object} Validation rules
   */
  getValidationRules() {
    return this.config.validation;
  }

  /**
   * Get cache configuration
   * @returns {Object} Cache configuration
   */
  getCacheConfig() {
    return this.config.cache;
  }

  /**
   * Get Cognito User Pool configuration
   * @returns {Object} Cognito configuration with userPoolId and clientId
   */
  getCognitoConfig() {
    // These values are set after SAM deployment
    // In production, these come from CloudFormation outputs
    const env = this.getEnvironment();
    
    // Check localStorage for custom override (useful for testing)
    const customUserPoolId = localStorage.getItem('cognitoUserPoolId');
    const customClientId = localStorage.getItem('cognitoClientId');
    
    if (customUserPoolId && customClientId) {
      return {
        userPoolId: customUserPoolId,
        clientId: customClientId,
        region: 'us-west-2'
      };
    }
    
    // Production Cognito config
    return {
      userPoolId: 'us-west-2_9Mk188OGa',
      clientId: '1qec97auhomp9ks0qu5r2rjc2v',
      region: 'us-west-2'
    };
  }

  /**
   * Reset configuration to defaults
   */
  resetConfig() {
    localStorage.removeItem('user-preferences');
    window.location.reload(); // Reload to reinitialize
  }

  /**
   * Export configuration as JSON
   * @returns {string} Configuration JSON
   */
  exportConfig() {
    return JSON.stringify(this.config, null, 2);
  }

  /**
   * Import configuration from JSON
   * @param {string} json - Configuration JSON
   */
  importConfig(json) {
    try {
      const imported = JSON.parse(json);
      this.mergeConfig(imported);
      this.saveUserPreferences(imported);
    } catch (error) {
      console.error('Failed to import configuration:', error);
      throw new Error('Invalid configuration format');
    }
  }
}

// Export singleton instance
const configServices = new ConfigServices();
export default configServices;

// Export bound convenience methods
export const getConfig = configServices.getConfig.bind(configServices);
export const getConfigSection = configServices.getConfigSection.bind(configServices);
export const get = configServices.get.bind(configServices);
export const set = configServices.set.bind(configServices);
export const getApiConfig = configServices.getApiConfig.bind(configServices);
export const getMapConfig = configServices.getMapConfig.bind(configServices);
export const getChartConfig = configServices.getChartConfig.bind(configServices);
export const getTableConfig = configServices.getTableConfig.bind(configServices);
export const getUIConfig = configServices.getUIConfig.bind(configServices);
export const getFeatureFlags = configServices.getFeatureFlags.bind(configServices);
export const isFeatureEnabled = configServices.isFeatureEnabled.bind(configServices);
export const getValidationRules = configServices.getValidationRules.bind(configServices);
export const getCacheConfig = configServices.getCacheConfig.bind(configServices);
export const getCognitoConfig = configServices.getCognitoConfig.bind(configServices);
export const saveUserPreferences = configServices.saveUserPreferences.bind(configServices);
export const resetConfig = configServices.resetConfig.bind(configServices);
export const exportConfig = configServices.exportConfig.bind(configServices);
export const importConfig = configServices.importConfig.bind(configServices);